import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, User as UserIcon, Mail, Hash, ShieldCheck, Wallet, Users as UsersIcon, KeyRound, Copy, Check, Pencil, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MODULES, moduleSubKeys, APPROVAL_PERMS, WALLET_PERMS } from "@/lib/modules";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-zinc-200`}>{value || <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [doc, setDoc] = useState(null);
  const [linkedWallet, setLinkedWallet] = useState(null);
  const [team, setTeam] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savingModule, setSavingModule] = useState(null);
  const [savingWalletId, setSavingWalletId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [savingVisibleUsers, setSavingVisibleUsers] = useState(null); // module key being saved
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleDraft, setRoleDraft] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamDraft, setTeamDraft] = useState(null);
  const [savingTeam, setSavingTeam] = useState(false);
  const [reportingOpen, setReportingOpen] = useState(false);
  const [reportingDraft, setReportingDraft] = useState(null);
  const [savingReporting, setSavingReporting] = useState(false);
  const [savingStatuses, setSavingStatuses] = useState(false);

  // Kept in-sync with backend `ISSUE_STATUSES` (Open → … → Closed).
  const TICKET_STATUSES = ["Open", "In Progress", "On Hold", "Resolved", "Verified", "Closed"];

  const isAdmin = me?.role === "admin";
  const enabledSet = useMemo(
    () => new Set(doc?.enabled_modules || []),
    [doc?.enabled_modules]
  );
  const visibleWalletSet = useMemo(
    () => new Set(doc?.visible_wallet_ids || []),
    [doc?.visible_wallet_ids]
  );
  // Per-module visible-user map: { tasks: ["uid1", "uid2"], ... }
  const visibleUsersMap = doc?.visible_user_ids || {};
  const canViewOthers = enabledSet.has("wallets_view_others") || enabledSet.has("view_others_wallet");

  const persistUser = async (patch) => {
    try {
      const { data } = await api.patch(`/users/${id}`, patch);
      setDoc(data);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
      try { const { data } = await api.get(`/users/${id}`); setDoc(data); } catch (_) { /* ignore */ }
      return false;
    }
  };

  const toggleVisibleWallet = async (walletId) => {
    if (!isAdmin) return;
    const next = new Set(visibleWalletSet);
    if (next.has(walletId)) next.delete(walletId); else next.add(walletId);
    const nextList = Array.from(next);
    setSavingWalletId(walletId);
    setDoc((d) => ({ ...d, visible_wallet_ids: nextList })); // optimistic
    await persistUser({ visible_wallet_ids: nextList });
    setSavingWalletId(null);
  };

  // Bulk-set the visible-user list for a single module. Used by both the
  // per-row toggle and the "Select All" checkbox in the module card.
  const setVisibleUsersForModule = async (moduleKey, nextIds) => {
    if (!isAdmin) return;
    setSavingVisibleUsers(moduleKey);
    const nextMap = { ...visibleUsersMap, [moduleKey]: nextIds };
    setDoc((d) => ({ ...d, visible_user_ids: nextMap })); // optimistic
    await persistUser({ visible_user_ids: nextMap });
    setSavingVisibleUsers(null);
  };

  const setVisibleTicketStatuses = async (nextList) => {
    if (!isAdmin) return;
    setSavingStatuses(true);
    setDoc((d) => ({ ...d, visible_ticket_statuses: nextList })); // optimistic
    await persistUser({ visible_ticket_statuses: nextList });
    setSavingStatuses(false);
  };

  const toggleModule = async (key) => {
    if (!isAdmin) return;
    const next = new Set(enabledSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    const nextList = Array.from(next).sort();
    setSavingModule(key);
    setDoc((d) => ({ ...d, enabled_modules: nextList })); // optimistic
    await persistUser({ enabled_modules: nextList });
    setSavingModule(null);
  };

  const generatePassword = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/users/${id}/reset-password`);
      setNewPassword(data.password);
      setCopied(false);
      toast.success("New password generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to generate password");
    } finally {
      setGenerating(false);
    }
  };

  const copyPassword = async () => {
    if (!newPassword) return;
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
      toast.success("Password copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copy failed — please copy manually");
    }
  };

  const saveRole = async () => {
    if (!isAdmin || !roleDraft || roleDraft === doc.role) {
      setRoleOpen(false);
      return;
    }
    setSavingRole(true);
    const ok = await persistUser({ role: roleDraft });
    setSavingRole(false);
    if (ok) {
      toast.success("Role updated");
      setRoleOpen(false);
    }
  };

  const saveTeam = async () => {
    if (!isAdmin) { setTeamOpen(false); return; }
    const targetId = teamDraft || null; // null = remove from team
    const currentId = team?.id || null;
    if (targetId === currentId) { setTeamOpen(false); return; }
    setSavingTeam(true);
    try {
      // Remove from old team (members + clear team_lead_id if matches).
      if (team) {
        const nextMembers = (team.members || []).filter((mid) => mid !== doc.id);
        const patch = { members: nextMembers };
        if (team.team_lead_id === doc.id) patch.team_lead_id = null;
        await api.patch(`/teams/${team.id}`, patch);
      }
      // Add to new team.
      if (targetId) {
        const nextTeam = allTeams.find((t) => t.id === targetId);
        const nextMembers = Array.from(new Set([...(nextTeam?.members || []), doc.id]));
        await api.patch(`/teams/${targetId}`, { members: nextMembers });
      }
      await load();
      toast.success(targetId ? "Team updated" : "Removed from team");
      setTeamOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Team update failed");
    } finally {
      setSavingTeam(false);
    }
  };

  const saveReporting = async () => {
    if (!isAdmin) { setReportingOpen(false); return; }
    const next = reportingDraft || "";
    const current = doc.reporting_to_id || "";
    if (next === current) { setReportingOpen(false); return; }
    setSavingReporting(true);
    // Empty string clears the field on the backend.
    const ok = await persistUser({ reporting_to_id: next });
    setSavingReporting(false);
    if (ok) {
      toast.success(next ? "Reporting manager updated" : "Reporting manager cleared");
      setReportingOpen(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: u }, { data: wallets }, { data: teams }, { data: usersList }] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/wallets`),
        api.get(`/teams`),
        api.get(`/users`),
      ]);
      setDoc(u);
      setAllWallets(wallets || []);
      setAllTeams(teams || []);
      setAllUsers((usersList || []).filter((x) => x.id !== u.id)); // exclude self
      // Wallet — prefer wallet tagged via wallets.user_id; fall back to matching wallet_number.
      const byTag = (wallets || []).find((w) => w.user_id === u.id);
      const byNumber = !byTag && u.wallet_number
        ? (wallets || []).find((w) => (w.wallet_number || "").trim() === (u.wallet_number || "").trim())
        : null;
      setLinkedWallet(byTag || byNumber || null);
      // Team — lead OR member.
      const t = (teams || []).find(
        (x) => x.team_lead_id === u.id || (Array.isArray(x.members) && x.members.includes(u.id))
      );
      setTeam(t || null);
    } catch (e) {
      toast.error("User not found");
      navigate("/teams-users#users");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading user…</div>;
  }
  if (!doc) return null;

  const walletNumberText = linkedWallet?.wallet_number || doc.wallet_number || null;
  const walletBalance = linkedWallet?.closing_balance;

  const renderWalletNumber = (cls = "font-mono text-emerald-400") => {
    if (!walletNumberText) return <span className="text-zinc-600">—</span>;
    if (linkedWallet) {
      return (
        <Link
          to={`/wallets/${linkedWallet.id}`}
          className={`${cls} hover:underline cursor-pointer`}
          data-testid="user-detail-wallet-link"
          title="Open wallet transactions"
        >
          {walletNumberText}
        </Link>
      );
    }
    return <span className={cls}>{walletNumberText}</span>;
  };

  return (
    <div className="space-y-8" data-testid="user-detail-page">
      <Link to="/teams-users#users" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Back to Teams &amp; Users
      </Link>
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            <span className="font-mono normal-case tracking-normal" data-testid="user-detail-code">
              EMP ID: {doc.code || "—"}
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }} data-testid="user-detail-name">
            <UserIcon className="w-7 h-7 text-sky-400" /> {doc.name || doc.email}
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />{doc.email}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              {isAdmin ? (
                <Popover open={roleOpen} onOpenChange={(o) => { setRoleOpen(o); if (o) setRoleDraft(doc.role || "team_associate"); }}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="capitalize inline-flex items-center gap-1 rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 text-zinc-300 hover:bg-white/5 hover:text-white cursor-pointer transition-colors"
                      data-testid="user-detail-role-edit-btn"
                      title="Click to change role"
                    >
                      {(doc.role || "team_associate").replace(/_/g, " ")}
                      <Pencil className="w-3 h-3 opacity-60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-64 bg-zinc-950 border-white/10 text-zinc-100"
                    data-testid="user-detail-role-popover"
                  >
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Change Role</div>
                    <Select value={roleDraft || ""} onValueChange={setRoleDraft}>
                      <SelectTrigger className="bg-zinc-900 border-white/10 text-zinc-100" data-testid="user-detail-role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                        <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                        <SelectItem value="team_lead" data-testid="role-option-team_lead">Team Lead</SelectItem>
                        <SelectItem value="team_associate" data-testid="role-option-team_associate">Team Associate</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="ghost" size="sm" onClick={() => setRoleOpen(false)} disabled={savingRole} className="text-zinc-400 hover:text-white">Cancel</Button>
                      <Button
                        size="sm"
                        onClick={saveRole}
                        disabled={savingRole || !roleDraft || roleDraft === doc.role}
                        className="bg-sky-500 text-black hover:bg-sky-400 font-medium"
                        data-testid="user-detail-role-save-btn"
                      >
                        {savingRole && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                        Save
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="capitalize">{(doc.role || "team_associate").replace(/_/g, " ")}</span>
              )}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="user-detail-team">
              <UsersIcon className="w-3.5 h-3.5" />
              {isAdmin ? (
                <Popover open={teamOpen} onOpenChange={(o) => { setTeamOpen(o); if (o) setTeamDraft(team?.id || ""); }}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 text-zinc-300 hover:bg-white/5 hover:text-white cursor-pointer transition-colors"
                      data-testid="user-detail-team-edit-btn"
                      title="Click to change team"
                    >
                      {team?.name || <span className="text-zinc-600">No team</span>}
                      <Pencil className="w-3 h-3 opacity-60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-64 bg-zinc-950 border-white/10 text-zinc-100"
                    data-testid="user-detail-team-popover"
                  >
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Change Team</div>
                    <Select value={teamDraft || "__none__"} onValueChange={(v) => setTeamDraft(v === "__none__" ? "" : v)}>
                      <SelectTrigger className="bg-zinc-900 border-white/10 text-zinc-100" data-testid="user-detail-team-select">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-64">
                        <SelectItem value="__none__" data-testid="team-option-none">No team</SelectItem>
                        {allTeams.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`team-option-${t.id}`}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {team?.team_lead_id === doc.id && (teamDraft || "") !== (team?.id || "") && (
                      <p className="text-[11px] text-amber-400/80 mt-2">
                        This user is the lead of {team.name}. Moving them will clear that team&apos;s lead.
                      </p>
                    )}
                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="ghost" size="sm" onClick={() => setTeamOpen(false)} disabled={savingTeam} className="text-zinc-400 hover:text-white">Cancel</Button>
                      <Button
                        size="sm"
                        onClick={saveTeam}
                        disabled={savingTeam || (teamDraft || "") === (team?.id || "")}
                        className="bg-sky-500 text-black hover:bg-sky-400 font-medium"
                        data-testid="user-detail-team-save-btn"
                      >
                        {savingTeam && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                        Save
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="text-zinc-300">{team?.name || <span className="text-zinc-600">No team</span>}</span>
              )}
            </span>
            {/* Reporting To — admin-only editor, hidden when the user's role is admin
                (admins don't report to anyone in this hierarchy). The dropdown
                only lists admin + team-lead users so the reporting chain stays
                upward. */}
            {doc.role !== "admin" && (() => {
              const eligible = allUsers.filter((u) => u.role === "admin" || u.role === "team_lead");
              const current = eligible.find((u) => u.id === doc.reporting_to_id)
                || (doc.reporting_to_id ? { id: doc.reporting_to_id, name: "(unknown user)" } : null);
              const currentLabel = current?.name || current?.email;
              return (
                <span className="inline-flex items-center gap-1.5" data-testid="user-detail-reporting">
                  <UserCheck className="w-3.5 h-3.5" />
                  <span className="text-zinc-500 normal-case">Reports to:</span>
                  {isAdmin ? (
                    <Popover open={reportingOpen} onOpenChange={(o) => { setReportingOpen(o); if (o) setReportingDraft(doc.reporting_to_id || ""); }}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 text-zinc-300 hover:bg-white/5 hover:text-white cursor-pointer transition-colors"
                          data-testid="user-detail-reporting-edit-btn"
                          title="Click to change reporting manager"
                        >
                          {currentLabel || <span className="text-zinc-600">Not set</span>}
                          <Pencil className="w-3 h-3 opacity-60" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-72 bg-zinc-950 border-white/10 text-zinc-100"
                        data-testid="user-detail-reporting-popover"
                      >
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Change Reporting Manager</div>
                        <Select value={reportingDraft || "__none__"} onValueChange={(v) => setReportingDraft(v === "__none__" ? "" : v)}>
                          <SelectTrigger className="bg-zinc-900 border-white/10 text-zinc-100" data-testid="user-detail-reporting-select">
                            <SelectValue placeholder="Select reporting manager" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
                            <SelectItem value="__none__" data-testid="reporting-option-none">No reporting manager</SelectItem>
                            {eligible.length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-zinc-500 italic">No admins or team-leads available</div>
                            ) : eligible.map((u) => (
                              <SelectItem key={u.id} value={u.id} data-testid={`reporting-option-${u.id}`}>
                                {u.name || u.email}
                                <span className="ml-2 text-[10px] text-zinc-500 capitalize">{(u.role || "").replace(/_/g, " ")}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-zinc-500 mt-2">
                          When this user&apos;s <span className="text-zinc-300">OPA Approval for Self</span> is off,
                          their OPA requests will route to this manager&apos;s approval queue.
                        </p>
                        <div className="flex justify-end gap-2 mt-3">
                          <Button variant="ghost" size="sm" onClick={() => setReportingOpen(false)} disabled={savingReporting} className="text-zinc-400 hover:text-white">Cancel</Button>
                          <Button
                            size="sm"
                            onClick={saveReporting}
                            disabled={savingReporting || (reportingDraft || "") === (doc.reporting_to_id || "")}
                            className="bg-sky-500 text-black hover:bg-sky-400 font-medium"
                            data-testid="user-detail-reporting-save-btn"
                          >
                            {savingReporting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                            Save
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span className="text-zinc-300">{currentLabel || <span className="text-zinc-600">Not set</span>}</span>
                  )}
                </span>
              );
            })()}
            <span className="inline-flex items-center gap-1.5" data-testid="user-detail-balance">
              <Wallet className="w-3.5 h-3.5" />
              {linkedWallet ? (
                <Link
                  to={`/wallets/${linkedWallet.id}`}
                  className="font-mono text-emerald-400 hover:underline cursor-pointer"
                  data-testid="user-detail-balance-link"
                  title="Open wallet transactions"
                >
                  {fmtINR(walletBalance)}
                </Link>
              ) : (
                <span className="font-mono text-zinc-600">—</span>
              )}
            </span>
          </div>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { setNewPassword(null); setCopied(false); setResetOpen(true); }}
            className="bg-amber-500 text-black hover:bg-amber-400 font-medium"
            data-testid="user-generate-password-btn"
          >
            <KeyRound className="w-4 h-4 mr-1.5" /> Generate New Password
          </Button>
        )}
      </header>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="label-overline mb-3">Contact</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <Row label="EMP ID" value={doc.code} mono />
          <Row label="Email" value={doc.email} />
          <Row label="Contact Number" value={doc.contact_number} mono />
          <Row label="Extension" value={doc.extension_number} mono />
          <Row label="Wallet Number" value={renderWalletNumber("font-mono text-emerald-400")} />
        </div>
        {linkedWallet && (
          <div className="mt-3 text-[11px] text-zinc-500">
            Click the wallet number to view all transactions for{" "}
            <span className="text-zinc-300">{linkedWallet.holder_name}</span>.
          </div>
        )}
      </section>

      {isAdmin && (
        <section className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid="user-modules-section">
          <div className="label-overline mb-3 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Module Access &amp; Approval Permissions
          </div>
          <p className="text-xs text-zinc-500 mb-5">
            Toggle which modules this user can access. Each module has scope sub-permissions
            (Teammates / Other Users) that appear once the module is enabled.
            Admins always see everything regardless of these toggles.
          </p>

          {/* Per-module cards */}
          <div className="space-y-4">
            {MODULES.map((mod) => {
              const isOn = enabledSet.has(mod.key);
              const isReadOnly = enabledSet.has(`${mod.key}_readonly`);
              const subs = moduleSubKeys(mod);
              return (
                <div
                  key={mod.key}
                  className={`rounded-lg border ${isOn ? "border-white/15 bg-zinc-950" : "border-white/5 bg-zinc-950/40"} transition-colors`}
                  data-testid={`user-module-card-${mod.key}`}
                >
                  {/* Header: Module toggle + Read Only toggle */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5">
                    <label className="flex items-center gap-3 cursor-pointer min-w-0">
                      <Switch
                        checked={isOn}
                        onCheckedChange={() => toggleModule(mod.key)}
                        disabled={savingModule === mod.key}
                        data-testid={`user-module-switch-${mod.key}`}
                      />
                      <span className="text-sm font-medium text-zinc-100">{mod.label}</span>
                    </label>
                    <label
                      className={`flex items-center gap-2 ${isOn ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
                      title={!isOn ? "Enable the module first" : "Hides all create/update/delete UI for this module"}
                    >
                      <span className="text-[11px] uppercase tracking-wider text-zinc-500">Read only</span>
                      <Switch
                        checked={isReadOnly}
                        disabled={!isOn || savingModule === `${mod.key}_readonly`}
                        onCheckedChange={() => toggleModule(`${mod.key}_readonly`)}
                        data-testid={`user-module-switch-${mod.key}_readonly`}
                      />
                    </label>
                  </div>

                  {/* Sub-permissions — visible only when module is ON */}
                  {isOn && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-4 py-3" data-testid={`user-module-subs-${mod.key}`}>
                      {subs.map((s) => {
                        const subOn = enabledSet.has(s.key);
                        // When the module is in Read Only mode, the write sub-permissions
                        // (reply/edit) are visually disabled to make the constraint clear.
                        const isWriteSub = /_(?:reply|edit)_/.test(s.key);
                        const disabled = (isReadOnly && isWriteSub) || savingModule === s.key;
                        return (
                          <label
                            key={s.key}
                            className={`flex items-center justify-between gap-3 rounded-md border border-white/5 bg-zinc-900/60 px-3 py-2 ${
                              disabled ? "opacity-40 cursor-not-allowed" : "hover:border-white/20 cursor-pointer"
                            }`}
                            data-testid={`user-module-sub-${s.key}`}
                            title={isReadOnly && isWriteSub ? "Disabled while Read Only is on" : undefined}
                          >
                            <span className="text-[13px] text-zinc-300 truncate">{s.label}</span>
                            <Switch
                              checked={subOn}
                              disabled={disabled}
                              onCheckedChange={() => toggleModule(s.key)}
                              data-testid={`user-module-switch-${s.key}`}
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* Visible Users picker — applies to any module whose
                      `<mod>_view_others`, `<mod>_reply_others`, or
                      `<mod>_edit_others` sub is on. The same picker drives
                      the scope for every "others" permission on the module.
                      Wallets is excluded because it has its own wallet-specific
                      picker below. */}
                  {isOn && mod.key !== "wallets" && (
                    enabledSet.has(`${mod.key}_view_others`) ||
                    enabledSet.has(`${mod.key}_reply_others`) ||
                    enabledSet.has(`${mod.key}_edit_others`)
                  ) && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid={`user-visible-users-${mod.key}`}>
                      {(() => {
                        const moduleVisibleSet = new Set(visibleUsersMap[mod.key] || []);
                        const allSelected = allUsers.length > 0 && allUsers.every((u) => moduleVisibleSet.has(u.id));
                        const someSelected = !allSelected && allUsers.some((u) => moduleVisibleSet.has(u.id));
                        const saving = savingVisibleUsers === mod.key;
                        const onSelectAll = () => {
                          if (allSelected) setVisibleUsersForModule(mod.key, []);
                          else setVisibleUsersForModule(mod.key, allUsers.map((u) => u.id));
                        };
                        const onToggleOne = (uid) => {
                          const next = new Set(moduleVisibleSet);
                          if (next.has(uid)) next.delete(uid); else next.add(uid);
                          setVisibleUsersForModule(mod.key, Array.from(next));
                        };
                        return (
                          <>
                            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
                                <UsersIcon className="w-3 h-3" /> Visible Users
                                <span className="ml-1 normal-case tracking-normal text-zinc-500">
                                  ({moduleVisibleSet.size}/{allUsers.length})
                                </span>
                              </div>
                              <label className="flex items-center gap-2 cursor-pointer text-[11px]" data-testid={`user-visible-users-${mod.key}-select-all`}>
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                  onChange={onSelectAll}
                                  disabled={saving || allUsers.length === 0}
                                  className="accent-emerald-500 w-3.5 h-3.5"
                                />
                                <span className="text-zinc-300">Select All</span>
                              </label>
                            </div>
                            <p className="text-[11px] text-zinc-500 mb-3">
                              Pick which other users&apos; items this user can see in <span className="text-zinc-300">{mod.label}</span>.
                            </p>
                            {allUsers.length === 0 ? (
                              <div className="text-sm text-zinc-500 italic">No other users to grant access to.</div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
                                {allUsers.map((u) => {
                                  const checked = moduleVisibleSet.has(u.id);
                                  return (
                                    <label
                                      key={u.id}
                                      className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer text-[12px] ${
                                        checked ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-zinc-900/60 border border-white/5 hover:border-white/20"
                                      }`}
                                      data-testid={`user-visible-user-${mod.key}-${u.id}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => onToggleOne(u.id)}
                                        disabled={saving}
                                        className="accent-emerald-500 w-3.5 h-3.5 shrink-0"
                                      />
                                      <span className="truncate text-zinc-200">{u.name || u.email}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Ticket Close Approvers — shown when the ticket close
                      approval flow is enabled on this user. Picks the users
                      who are authorised to close this user's tickets. */}
                  {isOn && mod.key === "issues" && enabledSet.has("approvals_ticket") && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid="user-ticket-close-approvers">
                      {(() => {
                        const approverSet = new Set(visibleUsersMap["approvals_ticket"] || []);
                        const allSelected = allUsers.length > 0 && allUsers.every((u) => approverSet.has(u.id));
                        const someSelected = !allSelected && allUsers.some((u) => approverSet.has(u.id));
                        const saving = savingVisibleUsers === "approvals_ticket";
                        const onSelectAll = () => {
                          if (allSelected) setVisibleUsersForModule("approvals_ticket", []);
                          else setVisibleUsersForModule("approvals_ticket", allUsers.map((u) => u.id));
                        };
                        const onToggleOne = (uid) => {
                          const next = new Set(approverSet);
                          if (next.has(uid)) next.delete(uid); else next.add(uid);
                          setVisibleUsersForModule("approvals_ticket", Array.from(next));
                        };
                        return (
                          <>
                            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-300/80 font-medium">
                                <ShieldCheck className="w-3 h-3" /> Ticket Close Approvers
                                <span className="ml-1 normal-case tracking-normal text-zinc-500">
                                  ({approverSet.size}/{allUsers.length})
                                </span>
                              </div>
                              <label className="flex items-center gap-2 cursor-pointer text-[11px]" data-testid="user-ticket-close-approvers-select-all">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                  onChange={onSelectAll}
                                  disabled={saving || allUsers.length === 0}
                                  className="accent-amber-400 w-3.5 h-3.5"
                                />
                                <span className="text-zinc-300">Select All</span>
                              </label>
                            </div>
                            <p className="text-[11px] text-zinc-500 mb-3">
                              Pick the users who can perform the <span className="text-zinc-300">Close Ticket</span> action on
                              tickets raised by <span className="text-zinc-300">{doc.name}</span>.
                              Nobody outside this list will be able to move a ticket into <span className="text-zinc-300">Closed</span>.
                            </p>
                            {allUsers.length === 0 ? (
                              <div className="text-sm text-zinc-500 italic">No other users to designate as approvers.</div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
                                {allUsers.map((u) => {
                                  const checked = approverSet.has(u.id);
                                  return (
                                    <label
                                      key={u.id}
                                      className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer text-[12px] ${
                                        checked
                                          ? "bg-amber-500/10 border border-amber-500/30"
                                          : "bg-zinc-900/60 border border-white/5 hover:border-white/20"
                                      }`}
                                      data-testid={`user-ticket-close-approver-${u.id}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => onToggleOne(u.id)}
                                        disabled={saving}
                                        className="accent-amber-400 w-3.5 h-3.5 shrink-0"
                                      />
                                      <span className="truncate text-zinc-200">{u.name || u.email}</span>
                                      <span className="ml-auto text-[10px] text-zinc-500 capitalize">
                                        {(u.role || "").replace(/_/g, " ")}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Visible Ticket Statuses — shown when the status-change
                      permission is enabled on this user. Picks which of the
                      6 lifecycle statuses appear in the reply form's status
                      dropdown for this user. */}
                  {isOn && mod.key === "issues" && enabledSet.has("issues_status_change") && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid="user-visible-ticket-statuses">
                      {(() => {
                        const allowed = new Set(doc.visible_ticket_statuses || []);
                        const allSelected = TICKET_STATUSES.every((s) => allowed.has(s));
                        const someSelected = !allSelected && TICKET_STATUSES.some((s) => allowed.has(s));
                        const onSelectAll = () => {
                          if (allSelected) setVisibleTicketStatuses([]);
                          else setVisibleTicketStatuses([...TICKET_STATUSES]);
                        };
                        const onToggleOne = (s) => {
                          const next = new Set(allowed);
                          if (next.has(s)) next.delete(s); else next.add(s);
                          setVisibleTicketStatuses(Array.from(next));
                        };
                        return (
                          <>
                            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-sky-300/80 font-medium">
                                <ShieldCheck className="w-3 h-3" /> Allowed Ticket Statuses
                                <span className="ml-1 normal-case tracking-normal text-zinc-500">
                                  ({allowed.size}/{TICKET_STATUSES.length})
                                </span>
                              </div>
                              <label className="flex items-center gap-2 cursor-pointer text-[11px]" data-testid="user-visible-ticket-statuses-select-all">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                  onChange={onSelectAll}
                                  disabled={savingStatuses}
                                  className="accent-sky-400 w-3.5 h-3.5"
                                />
                                <span className="text-zinc-300">Select All</span>
                              </label>
                            </div>
                            <p className="text-[11px] text-zinc-500 mb-3">
                              Only the ticked statuses will appear in the <span className="text-zinc-300">Status</span> dropdown
                              when <span className="text-zinc-300">{doc.name}</span> is replying on a ticket. Leave all
                              unchecked to hide the dropdown entirely.
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-1.5">
                              {TICKET_STATUSES.map((s) => {
                                const checked = allowed.has(s);
                                return (
                                  <label
                                    key={s}
                                    className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer text-[12px] ${
                                      checked
                                        ? "bg-sky-500/10 border border-sky-500/30"
                                        : "bg-zinc-900/60 border border-white/5 hover:border-white/20"
                                    }`}
                                    data-testid={`user-visible-ticket-status-${s.replace(/\s+/g, "-")}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => onToggleOne(s)}
                                      disabled={savingStatuses}
                                      className="accent-sky-400 w-3.5 h-3.5 shrink-0"
                                    />
                                    <span className="truncate text-zinc-200">{s}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Wallets module — inline "Visible Wallets" picker (same
                      checkbox + Select All UX as the per-module user picker). */}
                  {isOn && mod.key === "wallets" && canViewOthers && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid="user-visible-wallets-section">
                      {(() => {
                        const selectable = allWallets.filter((w) => w.user_id !== doc.id);
                        const allSelected = selectable.length > 0 && selectable.every((w) => visibleWalletSet.has(w.id));
                        const someSelected = !allSelected && selectable.some((w) => visibleWalletSet.has(w.id));
                        const onSelectAll = async () => {
                          const next = allSelected ? [] : selectable.map((w) => w.id);
                          setSavingWalletId("__all__");
                          setDoc((d) => ({ ...d, visible_wallet_ids: next }));
                          await persistUser({ visible_wallet_ids: next });
                          setSavingWalletId(null);
                        };
                        return (
                          <>
                            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
                                <Wallet className="w-3 h-3" /> Visible Wallets
                                <span className="ml-1 normal-case tracking-normal text-zinc-500">
                                  ({visibleWalletSet.size}/{selectable.length})
                                </span>
                              </div>
                              <label className="flex items-center gap-2 cursor-pointer text-[11px]" data-testid="user-visible-wallets-select-all">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                  onChange={onSelectAll}
                                  disabled={savingWalletId === "__all__" || selectable.length === 0}
                                  className="accent-emerald-500 w-3.5 h-3.5"
                                />
                                <span className="text-zinc-300">Select All</span>
                              </label>
                            </div>
                            <p className="text-[11px] text-zinc-500 mb-3">
                              Pick which wallets this user can see under <span className="text-zinc-300">Wallets → Team Wallets</span>.
                              Their own wallet is always accessible.
                            </p>
                            {allWallets.length === 0 ? (
                              <div className="text-sm text-zinc-500 italic">No wallets exist yet.</div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
                                {allWallets.map((w) => {
                                  const isOwn = w.user_id === doc.id;
                                  const checked = isOwn || visibleWalletSet.has(w.id);
                                  return (
                                    <label
                                      key={w.id}
                                      className={`flex items-center gap-2 rounded px-2 py-1.5 text-[12px] ${
                                        isOwn
                                          ? "bg-zinc-900/40 border border-white/5 opacity-60 cursor-not-allowed"
                                          : checked
                                          ? "bg-emerald-500/10 border border-emerald-500/30 cursor-pointer"
                                          : "bg-zinc-900/60 border border-white/5 hover:border-white/20 cursor-pointer"
                                      }`}
                                      data-testid={`user-visible-wallet-${w.id}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleVisibleWallet(w.id)}
                                        disabled={isOwn || savingWalletId === w.id || savingWalletId === "__all__"}
                                        className="accent-emerald-500 w-3.5 h-3.5 shrink-0"
                                      />
                                      <div className="min-w-0">
                                        <div className="text-zinc-200 truncate">
                                          {w.holder_name} {isOwn && <span className="text-[10px] text-zinc-500">(own)</span>}
                                        </div>
                                        <div className="text-[10px] text-zinc-500 font-mono truncate">{w.wallet_number}</div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) { setNewPassword(null); setCopied(false); } }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-md" data-testid="user-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <KeyRound className="w-5 h-5 text-amber-400" /> Generate New Password
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {!newPassword
                ? <>This will replace the existing password for <span className="text-zinc-300">{doc.name}</span> ({doc.email}). The user&apos;s current sessions remain active until logout.</>
                : <>The new password is shown <span className="text-amber-300 font-medium">only once</span>. Copy it now and share it with the user securely.</>}
            </DialogDescription>
          </DialogHeader>

          {newPassword ? (
            <div className="mt-3 space-y-3">
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-1">New Password</div>
                <div className="flex items-center justify-between gap-3">
                  <code
                    className="text-lg font-mono font-semibold text-white select-all break-all"
                    data-testid="user-new-password"
                  >
                    {newPassword}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyPassword}
                    className="text-zinc-300 hover:text-white shrink-0"
                    data-testid="user-copy-password"
                  >
                    {copied ? <Check className="w-4 h-4 mr-1.5 text-emerald-400" /> : <Copy className="w-4 h-4 mr-1.5" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                Make sure to deliver the password via a secure channel. After closing this dialog the password cannot be retrieved again — only a new one can be generated.
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-zinc-400">
              Click <span className="text-zinc-200 font-medium">Generate</span> to create a new 12-character password.
            </div>
          )}

          <DialogFooter className="mt-4">
            {!newPassword ? (
              <>
                <Button variant="ghost" onClick={() => setResetOpen(false)} disabled={generating} className="text-zinc-400 hover:text-white">Cancel</Button>
                <Button
                  onClick={generatePassword}
                  disabled={generating}
                  className="bg-amber-500 text-black hover:bg-amber-400 font-medium"
                  data-testid="user-confirm-generate-password"
                >
                  {generating && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                  Generate
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setResetOpen(false)}
                className="bg-white text-black hover:bg-zinc-200 font-medium"
                data-testid="user-close-password-dialog"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
